import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../UserContext';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import Footer from '../Footer';
import styles from './ContactUs.module.css';


import StudentNav from '../Navbars/StudentNav';
import AdminNav from '../Navbars/AdminNav';
import TeacherNav from '../Navbars/TeacherNav';
import contactIcon from '../../assets/contacticon.png';
import contactIcon1 from '../../assets/contacticon1.png';
import submitIcon from '../../assets/submit.png';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import apiService from '../../services/apiService';
import emailjs from '@emailjs/browser';

const ContactUs = () => {
  const form = useRef();
  const { isAdmin, isTeacher, isStudent, setUserRole, setUserInfo, userInfo } = useContext(UserContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    user_name: '',
    age_child: '',
    user_email: '',
    user_number: '',
    country: '',
    message: '',
    acceptTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryOptions] = useState(countryList().getData());
  const [selectedCountry, setSelectedCountry] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userData = await apiService.getCurrentUser();
        setUserInfo(userData);
        if (userData?.usertype) {
          setUserRole(userData.usertype);
        }
      } catch (err) {
        console.error('Error in useEffect:', err);
        // If user is not authenticated, set as guest
        setUserRole('guest');
      }
    };
    fetchData();
  }, [setUserInfo, setUserRole]);

  const onLogout = async () => {
    try {
      await apiService.logout();
      setUserInfo(null);
      setUserRole('guest');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API call fails
      setUserInfo(null);
      setUserRole('guest');
      navigate('/');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption);
    setFormData((prev) => ({
      ...prev,
      country: selectedOption ? selectedOption.label : '',
    }));
    if (errors.country) {
      setErrors((prev) => ({ ...prev, country: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.user_name.trim()) newErrors.user_name = 'Name is required';
    if (!formData.age_child || isNaN(formData.age_child) || formData.age_child <= 0)
      newErrors.age_child = 'Please enter a valid age';
    if (!formData.user_email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      newErrors.user_email = 'Please enter a valid email';
    if (!formData.user_number.match(/^\d{10,15}$/))
      newErrors.user_number = 'Please enter a valid phone number (10-15 digits)';
    if (!selectedCountry) newErrors.country = 'Please select a country';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (!formData.acceptTerms) newErrors.acceptTerms = 'You must accept the terms and conditions';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const formDataObj = new FormData(form.current);
        const data = {
          user_name: formDataObj.get('user_name'),
          age_child: formDataObj.get('age_child'),
          user_email: formDataObj.get('user_email'),
          user_number: formDataObj.get('user_number'),
          country: selectedCountry ? selectedCountry.label : '',
          message: formDataObj.get('message'),
        };
        const response = await fetch(`${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/contactus`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(data),
        });
        const result = await response.json();
        if (result.success) {
          toast.success(result.message || 'Message sent successfully! We will get back to you soon.', {
            position: 'top-center',
            autoClose: 3000,
          });
          form.current.reset();
          setSelectedCountry(null);
          setFormData({
            user_name: '',
            age_child: '',
            user_email: '',
            user_number: '',
            country: '',
            message: '',
            acceptTerms: false,
          });
          setErrors({});
        } else {
          toast.error(result.message || 'Failed to send message. Please try again.', {
            position: 'top-center',
            autoClose: 3000,
          });
        }
      } catch (error) {
        toast.error('An error occurred while sending your message. Please try again.', {
          position: 'top-center',
          autoClose: 3000,
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const username = userInfo?.username;
  const avatar = userInfo?.avatar;

  return (
    <>
      <Helmet>
        
        <meta name="description" content="Get in touch with the Polymath Kids team for questions, feedback, or assistance." />
        <meta name="keywords" content="Polymath Kids, contact us, education, learning, support" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {username ? (
        <>
          {isAdmin && <AdminNav username={username} avatar={avatar} onLogout={onLogout} />}
          {isTeacher && <TeacherNav username={username} avatar={avatar} onLogout={onLogout} />}
          {isStudent && <StudentNav username={username} avatar={avatar} onLogout={onLogout} />}
        </>
      ) : null}

      <section className={styles.contactHeader}>
        <div className={styles.contactHeaderContent}>
          <div className={styles.headerIconContainer}>
            <img src={contactIcon} alt="Contact Icon Left" className={`${styles.contactImage} ${styles.contactImageLeft}`} />
            <div className={styles.headerText}>
              <h1>
                Get in Touch with the <br />
                <span className={styles.highlight}>Polymath Kids Team</span>
              </h1>
              <p>
                Have questions, feedback, or need assistance? We're here to help you every step of the way—whether you're a parent, educator, or curious learner. Reach out to us anytime and we'll get back to you as soon as possible. Your journey with Polymath-Kids.com matters to us, and we're just a message away.
              </p>
            </div>
            <img src={contactIcon1} alt="Contact Icon Right" className={`${styles.contactImage} ${styles.contactImageRight}`} />
          </div>
        </div>
      </section>

      <section className={styles.contactForm}>
        <div className={styles.contactFormContent}>
          <h2>
            <span className={styles.formTitlePart1}>Need Help?</span>{' '}
            <span className={styles.formTitlePart2}>Contact Us Anytime</span>
          </h2>
          <form onSubmit={handleSubmit} noValidate ref={form}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="user_name">Name</label>
                <input
                  type="text"
                  id="user_name"
                  name="user_name"
                  value={formData.user_name}
                  onChange={handleInputChange}
                  required
                  placeholder="Name"
                  disabled={isSubmitting}
                />
                {errors.user_name && <span className={styles.errorMessage}>{errors.user_name}</span>}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="age_child">Age of Child</label>
                <input
                  type="number"
                  id="age_child"
                  name="age_child"
                  value={formData.age_child}
                  onChange={handleInputChange}
                  required
                  placeholder="Age of child"
                  disabled={isSubmitting}
                />
                {errors.age_child && <span className={styles.errorMessage}>{errors.age_child}</span>}
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="user_email">Email</label>
                <input
                  type="email"
                  id="user_email"
                  name="user_email"
                  value={formData.user_email}
                  onChange={handleInputChange}
                  required
                  placeholder="Email"
                  disabled={isSubmitting}
                />
                {errors.user_email && <span className={styles.errorMessage}>{errors.user_email}</span>}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="user_number">WhatsApp Number</label>
                <input
                  type="tel"
                  id="user_number"
                  name="user_number"
                  value={formData.user_number}
                  onChange={handleInputChange}
                  required
                  placeholder="WhatsApp Number"
                  disabled={isSubmitting}
                  onKeyDown={(event) => {
                    if (!/[0-9]/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Delete' && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Tab') {
                      event.preventDefault();
                    }
                  }}
                />
                {errors.user_number && <span className={styles.errorMessage}>{errors.user_number}</span>}
              </div>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="country">Choose Your Country</label>
              <Select
                id="country"
                name="country"
                options={countryOptions}
                value={selectedCountry}
                onChange={handleCountryChange}
                className={errors.country ? `${styles.error}` : ''}
                placeholder="Choose Your Country"
                isDisabled={isSubmitting}
                inputId="country-select"
              />
              {/* <input type="hidden" name="country" style={{ display: 'hidden' }} value={formData.country} /> */}
              {errors.country && <span className={styles.errorMessage}>{errors.country}</span>}
            </div>
            <div className={styles.formGroupFullWidth}>
              <label htmlFor="message">Message</label>
              <textarea
                className={styles.messageBox}
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                placeholder="Type your message"
                disabled={isSubmitting}
              />
              {errors.message && <span className={styles.errorMessage}>{errors.message}</span>}
            </div>
            <div className={styles.formGroupTerms}>
              <label>
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
                I accept all the terms and conditions.
              </label>
              {errors.acceptTerms && <span className={styles.errorMessage}>{errors.acceptTerms}</span>}
            </div>
            <div className={styles.contactsubmitContainer}>
            <button
            className={`${styles.contactsubmitButton} ${isSubmitting ? styles.disabled : ''}`}
            onClick={!isSubmitting ? handleSubmit : undefined}
            disabled={isSubmitting}
            >
            {isSubmitting ? "Sending..." : "SUBMIT"}
  </button>
</div>
          </form>
        </div>
      </section>

      <Footer />
      <ToastContainer />
    </>
  );
};

export default ContactUs;